/**
 * Phaser: the product card "LAST n LEFT" badge after a swatch pick.
 *
 * blocks/_product-card-gallery.liquid first renders the badge only when every in-stock
 * variant is low, with the total across them, and lists every low variant in
 * data-phaser-low-stock ("variantId:qty,variantId:qty"). When the shopper picks a swatch
 * on the card, Horizon fires variant:update from inside the card with the new variant.
 * This shows the badge for that variant's own count, or hides it when that variant is
 * not low.
 *
 * One listener on document (module scripts run once), so section reloads in the
 * theme editor never stack handlers.
 */

import { ThemeEvents } from '@theme/events';

const BADGE_SELECTOR = '[data-phaser-low-stock]';

/**
 * @param {HTMLElement} badge
 * @returns {Record<string, number>}
 */
function readStockMap(badge) {
  /** @type {Record<string, number>} */
  const map = {};
  for (const pair of (badge.dataset.phaserLowStock || '').split(',')) {
    const [id, qty] = pair.split(':');
    if (id && qty) map[id.trim()] = Number(qty);
  }
  return map;
}

/**
 * @param {HTMLElement} badge
 * @param {string | number | null | undefined} variantId
 */
function updateBadge(badge, variantId) {
  const quantity = variantId == null ? 0 : Number(readStockMap(badge)[String(variantId)]) || 0;

  if (quantity < 1) {
    badge.hidden = true;
    badge.textContent = '';
    return;
  }

  const label =
    quantity === 1
      ? badge.dataset.labelOne || 'LAST ONE'
      : (badge.dataset.labelMany || 'LAST [count] LEFT').replace('[count]', String(quantity));

  badge.textContent = label;
  badge.hidden = false;
}

document.addEventListener(ThemeEvents.variantUpdate, (event) => {
  if (!(event.target instanceof Element)) return;

  const card = event.target.closest('product-card');
  if (!card) return;

  const badge = card.querySelector(BADGE_SELECTOR);
  if (!(badge instanceof HTMLElement)) return;

  const detail = /** @type {CustomEvent} */ (event).detail;
  const productId = detail?.data?.productId;
  if (productId && badge.dataset.productId && String(productId) !== badge.dataset.productId) return;

  updateBadge(badge, detail?.resource?.id);
});
